/**
 * Fire-and-forget DDx feedback events (ddxFeedbackEvents). Never throws to callers.
 * @healthdesk/shared-utils does not depend on Firebase — pass compat Firestore + serverTimestamp from the app.
 */

/**
 * @typedef {Object} DdxFeedbackFirestoreCompat
 * @property {import('firebase/compat').default.firestore.Firestore} db
 * @property {() => import('firebase/compat').default.firestore.FieldValue} serverTimestamp
 */

/**
 * Writes a clinician feedback event to ddxFeedbackEvents.
 *
 * @param {DdxFeedbackFirestoreCompat} firestoreCompat
 * @param {Object} event
 * @param {string} event.sessionSource - 'chartmind' | 'symptom_checker'
 * @param {string} event.sessionId
 * @param {string} event.bundleHash
 * @param {string} event.eventType - see DDX_FEEDBACK_EVENT_TYPES
 * @param {string} [event.userId] - required by Firestore rules when using client SDK
 * @param {string} [event.diagnosisId]
 * @param {string} [event.diagnosisLabel]
 * @param {number} [event.previousRank]
 * @param {number} [event.newRank]
 * @param {string} [event.chiefComplaintId]
 * @param {string} [event.temporalBucket]
 * @param {number} [event.kbScore]
 * @param {number} [event.trustTier]
 * @param {boolean} [event.wasInCantMiss]
 * @param {Array} [event.finalDdx]
 */
export async function writeFeedbackEvent(firestoreCompat, event) {
  const db = firestoreCompat?.db;
  const serverTimestamp = firestoreCompat?.serverTimestamp;
  if (!db || typeof db.collection !== 'function' || typeof serverTimestamp !== 'function') {
    console.error('[DDx feedback] missing db or serverTimestamp (non-blocking)');
    return;
  }
  try {
    await db.collection('ddxFeedbackEvents').add({
      ...event,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('[DDx feedback] write failed (non-blocking):', err);
  }
}
